import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { message, Select, Spin, Tooltip } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { FaSearch } from "react-icons/fa";
import { FaXmark } from "react-icons/fa6";
import { PiExport } from "react-icons/pi";

import "./styles.scss";
import ManageFilters from "../../../assets/component/manage-filters";
import Breadcrumbs from "../../../assets/component/breadcrumbs";
import CustomTable from "../../../assets/component/table";
import { retrieveBatch, retrieveBatchReset } from "../../../store/slices/playbook-batch/retrieveBatch";
import { reportExport, reportExportReset } from "../../../store/slices/playbook-batch/reportExport";

const TableActions = ({ playbookBatchId, dispatch, progress }) => {
  const [activeRow, setActiveRow] = useState(null);

  const { isLoading: reportExportLoading } = useSelector(
    (state) => state?.reportExport
  );

  const handleExport = async (value) => {
    setActiveRow(value);
    const payload = {
      playbookBatchId: value,
    };
    await dispatch(reportExport(payload));
    setActiveRow(null);
  };

  const isExportDisabled = progress === "In Progress" || progress === "Pending";

  return (
    <div className="action-buttons-container">
      <Tooltip
        title={
          isExportDisabled ? "Export unavailable while in progress" : "Export"
        }
      >
        <div
          className={`action-icons ${isExportDisabled ? "disabled" : ""}`}
          onClick={
            !isExportDisabled ? () => handleExport(playbookBatchId) : null
          }
          style={{
            cursor: isExportDisabled ? "not-allowed" : "pointer",
            opacity: isExportDisabled ? 0.5 : 1,
          }}
        >
          <span className="action-exportIcon">
            {reportExportLoading && activeRow === playbookBatchId ? (
              <Spin size="small" />
            ) : (
              <PiExport />
            )}{" "}
          </span>{" "}
        </div>
      </Tooltip>
    </div>
  );
};

const ManageBatch = () => {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [searchValue, setSearchValue] = useState("");
  const [isSearchFilterApplied, setIsSearchFilterApplied] = useState(false);
  const [status, setStatus] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(true);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [formattedTableData, setFormattedTableData] = useState({
    dataSource: [],
    columns: [],
  });

  const dispatch = useDispatch();
  const { data: retrieveBatchData, isLoading: retrieveBatchLoading } = useSelector((state) => state?.retrieveBatch);
  const { success: reportExportSuccess, error: reportExportError } = useSelector((state) => state?.reportExport);

  const antIcon = <LoadingOutlined style={{ fontSize: 13, color: "white" }} spin />;

  const tableHeaderData = [
    {
      title: "Playbook Batch",
      dataIndex: "playbookBatch",
      key: "playbookBatch",
      width: "10%",
      sorter: {
        compare: (a, b) => a.playbookBatch.localeCompare(b.playbookBatch),
        multiple: 3,
      },
    },
    {
      title: "Project",
      dataIndex: "projectName",
      key: "projectName",
      width: "10%",
      sorter: {
        compare: (a, b) => a.projectName.localeCompare(b.projectName),
        multiple: 3,
      },
    },
    {
      title: "Entity",
      dataIndex: "entityIds",
      key: "entityIds",
      width: "10%",
      sorter: {
        compare: (a, b) => {
          const lengthA = Array.isArray(a.entityIds) ? a.entityIds.length : 0;
          const lengthB = Array.isArray(b.entityIds) ? b.entityIds.length : 0;
          return lengthA - lengthB;
        },
        multiple: 3,
      },
      render: (_, record) => {
        const columnItems = 30;
        const entityNames = record?.entityNames || [];
        const entityIds = record?.entityIds || [];

        if (entityIds.length === 0) return "-";

        const tooltipData = [];
        for (let i = 0; i < entityNames.length; i += columnItems) {
          tooltipData.push(entityNames.slice(i, i + columnItems));
        }

        const tooltipContent = (
          <div style={{ display: "flex", gap: "16px" }}>
            {tooltipData.map((chunk, index) => (
              <ul
                key={index}
                style={{ margin: 0, padding: 0, listStyle: "none" }}
              >
                {chunk.map((name, idx) => (
                  <li key={idx}>{name}</li>
                ))}
              </ul>
            ))}
          </div>
        );
        return (
          <>
            {record?.entityIds?.length > 0 ?
              (<Tooltip
                overlayClassName="custom-tooltip"
                title={tooltipContent}
                overlayStyle={{ maxWidth: tooltipData.length * 250 }}
              >
                {record?.entityIds?.length}
              </Tooltip>) : ""
            }
          </>
        );
      },
    },
    {
      title: "Year",
      dataIndex: "year",
      key: "year",
      width: "10%",
      sorter: {
        compare: (a, b) => {
          const getYearValue = (yearArr) => {
            if (!Array.isArray(yearArr) || yearArr.length === 0) return 0;
            const yearStr = yearArr[0];
            return parseInt(yearStr.replace("FY-", ""), 10) || 0;
          };

          const yearA = getYearValue(a.year);
          const yearB = getYearValue(b.year);

          return yearA - yearB;
        },
        multiple: 3,
      },
    },
    {
      title: "Folder Path",
      dataIndex: "folderPath",
      key: "folderPath",
      width: "10%",
      render: (_, record) => {
        const folderPath = record?.folderPath || [];
        const isExpanded = expandedRowKeys.includes(record.key);
        const folderPathNames = folderPath.map(path => path?.split("/").filter(Boolean).pop());
        if (folderPath?.length === 0) return "-";
        return (
          <div className="folderpath-names">
            {!isExpanded && folderPathNames[0]}
            {isExpanded && (
              <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                <li>{folderPathNames[0]}</li>
              </ul>)}
            {isExpanded && folderPath.length > 1 && (
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {folderPathNames.slice(1).map((name, idx) => (
              <li key={idx}>
                {name}
              </li>
            ))}
          </ul>
        )}
            {folderPath?.length > 1 && <div className="show-more" style={{ cursor: "pointer", color: "blue", textDecoration: "underline" }} onClick={() => handleDisplayFolderNames(record.key)}>{isExpanded ? "see less" : "see more"}</div>}
          </div>
        )
      }
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      width: "10%",
      sorter: {
        compare: (a, b) => a.progress.localeCompare(b.progress),
        multiple: 3,
      },
    },
    {
      title: "Questions Count",
      dataIndex: "playbookQuestionsCount",
      key: "playbookQuestionsCount",
      width: "10%",
      sorter: {
        compare: (a, b) => a.playbookQuestionsCount - b.playbookQuestionsCount,
        multiple: 3,
      },
    },

    {
      title: "Format",
      dataIndex: "format",
      key: "format",
      width: "10%",
      sorter: {
        compare: (a, b) => {
          const formatA = Array.isArray(a.format) ? a.format[0] : a.format || "";
          const formatB = Array.isArray(b.format) ? b.format[0] : b.format || "";
          return formatA.localeCompare(formatB);
        },
        multiple: 3,
      },
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      key: "createdBy",
      width: "10%",
      sorter: {
        compare: (a, b) => a.createdBy.localeCompare(b.createdBy),
        multiple: 3,
      },
    },
    {
      title: "Created Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: "10%",
      sorter: {
        compare: (a, b) => a.createdAt.localeCompare(b.createdAt),
        multiple: 3,
      },
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: "15%",
    },
  ];

  const statusFilterOptions = [
    { label: "Pending", value: "Pending" },
    { label: "In Progress", value: "In Progress" },
    { label: "Completed", value: "Completed" },
    { label: "Failed", value: "Failed" },
  ];

  const handleRetrieveBatch = ({ type }) => {
    dispatch(retrieveBatchReset())

    let payload = { page, limit, batchStatus: status || "", ...(isSearchFilterApplied && { searchValue }) };

    if (["SEARCH", "RESET"]?.includes(type)) {
      setPage(1);

      payload = {
        page: 1,
        limit,
        searchValue: (type === "SEARCH") ? searchValue : "",
        batchStatus: status || ""
      };
    }

    dispatch(retrieveBatch(payload));
  }

  const handleSearch = (e) => {
    const value = e?.target?.value || "";
    setSearchValue(value);

    if(!value?.trim() && isSearchFilterApplied) {
      handleSearchClear();
    }
  }

  const handleDisplayFolderNames = (key) => {
    setExpandedRowKeys((prev) =>
      prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key]
    );    
  }

  const handleKeyPress = (e) => {
    if (e?.key !== 'Enter' || e?.shiftKey) return;

    e?.preventDefault();
    const inputValue = e?.target?.value?.trim();

    if(inputValue) {
      handleRequestSearch();
    }
  }

  const handleSearchClear = () => {
    setSearchValue("")
    setIsSearchFilterApplied(false);
    handleRetrieveBatch({ type: "RESET" })
  }

  const handleRequestSearch = () => {
    if (searchValue?.trim()) {
      setIsSearchFilterApplied(true);
      handleRetrieveBatch({ type: "SEARCH" })
    }
  }

  const handleShowFiltersSection = () => {
    setShowFilters(!showFilters);
  };

  const handleStatusChange = (value) => {
    setStatus(value);
  };

  useEffect(() => {
    if (page && limit) {
      handleRetrieveBatch({ type: "PAGINATION" })
    }
  }, [page, limit, status]);

  useEffect(() => {
    if (reportExportSuccess) {
      message.success("Report Downloaded successfully");
      dispatch(reportExportReset());
    } else if (reportExportError) {
      message.error(reportExportError?.message);
      dispatch(reportExportReset());
    }
  }, [reportExportSuccess, reportExportError, dispatch]);

  useEffect(() => {
    const filteredData = retrieveBatchData?.responseData?.map((data, id) => {
      return {
        key: id,
        playbookBatch: data?.playbookBatchName,
        projectName: data?.projectMasterName || "-",
        entityIds: data?.entityIds || [],
        entityNames: data.entityNames || [],
        year: data?.year?.length ? data.year : "-",
        folderPath: data?.folderPath || "-",
        format: data?.reportFormat,
        progress: data?.playbookBatchStatus,
        playbookQuestionsCount: data?.playbookQuestionsCount,
        createdBy: data?.createdBy?.name,
        createdAt: data?.createdAt
          ? new Date(data.createdAt).toISOString().split("T")[0]
          : null,
        actions: (
          <TableActions
            key={data?.id}
            cell={data}
            playbookBatchId={data?.playbookBatchId}
            progress={data?.playbookBatchStatus}
            dispatch={dispatch}
          />
        ),
      };
    });

    setTotalCount(retrieveBatchData?.totalCount || 0);
    setFormattedTableData({
      columns: tableHeaderData,
      dataSource: filteredData,
    });
  }, [retrieveBatchData?.responseData, expandedRowKeys]);

  return (
    <div className="run-batch-main">
      <Breadcrumbs />
      <div className="run-batch-filters">
        {showFilters && (
          <div className="show-filters-section">
            <div className="run-batch-filters-section">
              <div className="input-wrapper">
                <div className="input-container search-input-container">
                  <input
                    className="search-input"
                    value={searchValue}
                    onChange={(e) => handleSearch(e)}
                    onKeyDown={(e) => handleKeyPress(e)}
                    placeholder="Search batch"
                  />
                  {searchValue && <FaXmark className="input-clear-icon" onClick={() => handleSearchClear()} />}
                  <div className="search-button" onClick={() => handleRequestSearch()}>
                    {retrieveBatchLoading ? (
                      <Spin className="loading-icon" indicator={antIcon} />
                    ) : (
                      <FaSearch className="button-icon search-icon" />
                    )}
                  </div>
                </div>
              </div>
              <div className="input-wrapper">
                <div className="input-label">
                  Status
                </div>
                <div className="input-container">
                  <Select
                    showSearch
                    allowClear
                    className="filter-select"
                    options={statusFilterOptions}
                    onChange={handleStatusChange}
                    value={status}
                    placeholder="Select Status"
                    optionFilterProp="label"
                    filterOption={(input, option) => option?.label?.toLowerCase()?.includes(input?.toLowerCase())}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <ManageFilters
          showFilters={setShowFilters}
          handleShowFiltersSection={handleShowFiltersSection}
        />
      </div>
      <div className="run-batch-table-section">
        <CustomTable
          page={page}
          limit={limit}
          setPage={setPage}
          setLimit={setLimit}
          tableData={formattedTableData}
          totalRows={totalCount}
          isLoading={retrieveBatchLoading}
        />
      </div>
    </div>
  );
};

export default ManageBatch;
